import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Layout, Tree, message, Spin, Button, Space, Avatar, Dropdown, Alert, List, Input, Select, Divider, Drawer, Grid } from 'antd';
import { PlusOutlined, UploadOutlined, UserOutlined, LogoutOutlined, LinkOutlined, SafetyCertificateOutlined, SyncOutlined, CheckSquareOutlined, BorderOutlined, MenuOutlined } from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import { getAgents, getAgent, getAdvertisers, getAdvertisersByAgents, getAuthUrl, syncAdvertisers, type Advertiser, type Agent } from './api';
import './App.css';

import TableSkeleton from './components/TableSkeleton';

const AdvertiserTable = lazy(() => import('./components/AdvertiserTable'));
const AgentManagerModal = lazy(() => import('./components/AgentManagerModal'));
const TaskCenter = lazy(() => import('./components/TaskCenter'));
const Login = lazy(() => import('./components/Login'));

const { Sider, Content, Header } = Layout;

const App: React.FC = () => {
  const [loading, setLoading] = useState(false); // Initial Agent Load
  const [tableLoading, setTableLoading] = useState(false); // Advertiser Table Load
  const [syncing, setSyncing] = useState(false); // Sync Button Loading
  const [agentIds, setAgentIds] = useState<number[]>([]);
  const [agentName, setAgentName] = useState('');
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [selectedAdvertisers, setSelectedAdvertisers] = useState<Advertiser[]>([]);
  const [treeData, setTreeData] = useState<DataNode[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
  const [agentManagerVisible, setAgentManagerVisible] = useState(false);
  const [taskCenterVisible, setTaskCenterVisible] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [noAgentHint, setNoAgentHint] = useState(false);
  const [agentSearchText, setAgentSearchText] = useState('');

  // 响应式断点：手机(<768) / 平板(768~992) / 桌面(>=992)
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const isDesktop = !!screens.lg;
  const [siderVisible, setSiderVisible] = useState(false);

  useEffect(() => {
    // 检查是否已登录
    const token = localStorage.getItem('access_token');
    const savedUsername = localStorage.getItem('username');
    if (token && savedUsername) {
      setIsAuthenticated(true);
      setUsername(savedUsername);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  // 授权成功回调：从 URL 读取 oauth_success 并提示
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const oauthSuccess = params.get('oauth_success');
    const agentId = params.get('agent_id');
    const agentName = params.get('agent_name');
    if (oauthSuccess === '1' && agentId) {
      message.success(
        `二代「${decodeURIComponent(agentName || '')}」授权成功。请使用「批量导入」为该二代上传广告主 CSV，或手动「新增广告主」。`,
        8
      );
      window.history.replaceState({}, '', window.location.pathname);
      loadData();
    }
  }, [isAuthenticated]);

  // Rebuild treeData when agents or search text changes
  useEffect(() => {
    if (agents.length === 0) {
      setTreeData([]);
      return;
    }

    let filteredAgents = agents;
    if (agentSearchText) {
      const lowerSearch = agentSearchText.toLowerCase();
      filteredAgents = agents.filter(a => 
          (a.name && a.name.toLowerCase().includes(lowerSearch)) || 
          (a.root_advertiser_id && String(a.root_advertiser_id).toLowerCase().includes(lowerSearch))
      );
    }

    // Group by Customer Name
    const groupedAgents: Record<string, Agent[]> = {};
    filteredAgents.forEach(agent => {
        if (!groupedAgents[agent.name]) {
            groupedAgents[agent.name] = [];
        }
        groupedAgents[agent.name].push(agent);
    });

    const newTreeData: DataNode[] = Object.keys(groupedAgents).map(name => {
        const groupAgents = groupedAgents[name];
        const allAuth = groupAgents.every(a => a.has_token);
        const hasAuth = groupAgents.some(a => a.has_token);
        
        return {
            title: (
                <span>
                    {name}
                    {!allAuth && hasAuth && <span style={{color: '#faad14', fontSize: '12px', marginLeft: 4}}>(部分授权)</span>}
                    {!hasAuth && <span style={{color: '#ff4d4f', fontSize: '12px', marginLeft: 4}}>(未授权)</span>}
                </span>
            ),
            key: `group-${name}`,
            children: groupAgents.map(agent => ({
                title: (
                    <span>
                        {agent.root_advertiser_id || '无ID'}
                        {!agent.has_token && <span style={{color: '#faad14', fontSize: '12px', marginLeft: 4}}>(未授权)</span>}
                    </span>
                ),
                key: `agent-${agent.id}`,
                isLeaf: true
            }))
        };
    });

    setTreeData(newTreeData);
  }, [agents, agentSearchText]);

  const loadData = async (targetAgentIds?: number[]) => {
    // If we are just switching agents, we only need to load the table
    // If agents list is empty, we need to load agents (global loading)
    
    const isInitialLoad = agents.length === 0;
    
    if (isInitialLoad) {
        setLoading(true);
    } else {
        setTableLoading(true);
    }
    
    setNoAgentHint(false);
    try {
      let currentAgents = agents;
      if (currentAgents.length === 0) {
        const agentsRes = await getAgents();
        currentAgents = Array.isArray(agentsRes.data) ? agentsRes.data : [];
        setAgents(currentAgents);
      }
      
      // 如果没有代理商，清空数据
      if (currentAgents.length === 0) {
        setNoAgentHint(true);
        setAdvertisers([]);
        setSelectedAdvertisers([]);
        setAgentIds([]);
        setAgentName('');
        // Ensure loading state is reset
        if (isInitialLoad) setLoading(false);
        setTableLoading(false);
        return;
      }

      let ids = targetAgentIds !== undefined ? targetAgentIds : agentIds;
      
      setAgentIds(ids);
      
      // Update checked keys for UI
      const newCheckedKeys: React.Key[] = [];
      ids.forEach(id => newCheckedKeys.push(`agent-${id}`));
      setCheckedKeys(newCheckedKeys);

      if (ids.length > 0) {
          // 获取广告主列表 (Batch)
          const advRes = await getAdvertisersByAgents(ids);
          const advList: Advertiser[] = advRes.data.items;
          setAdvertisers(advList);
          setSelectedAdvertisers(advList);
          
          if (ids.length === 1) {
              const agent = currentAgents.find(a => a.id === ids[0]);
              setAgentName(agent?.name || '');
          } else {
              setAgentName(`已选择 ${ids.length} 个二代`);
          }
      } else {
          setAdvertisers([]);
          setSelectedAdvertisers([]);
          setAgentName('');
      }

    } catch (error: any) {
      const detail = error.response?.data?.detail ?? error.message;
      message.error(detail || '加载数据失败');
      setNoAgentHint(detail === '代理商不存在' || error.response?.status === 404);
      if (isInitialLoad) setAgents([]);
    } finally {
      if (isInitialLoad) setLoading(false);
      setTableLoading(false);
    }
  };

  const handleSyncAdvertisers = async () => {
    if (agentIds.length === 0) {
        message.warning('请先选择二代管家');
        return;
    }
    
    // Start background sync
    setSyncing(true);
    try {
      // Loop for each agent
      const syncPromises = agentIds.map(async (id) => {
        try {
          // This API call is fast (returns immediately)
          const res = await syncAdvertisers(id);
          return { success: true, agentId: id, message: res.data.message };
        } catch (error) {
          console.error(`触发同步代理商 ${id} 失败:`, error);
          return { success: false, agentId: id, error: error instanceof Error ? error.message : '未知错误' };
        }
      });
      
      const results = await Promise.all(syncPromises);
      
      const successCount = results.filter(r => r.success).length;
      const failedCount = results.filter(r => !r.success).length;
      
      if (successCount > 0) {
        message.success(`已触发 ${successCount} 个代理商的同步任务，请在任务中心查看进度或稍后刷新`, 5);
        // Do NOT call loadData() here to avoid blocking UI or confusing user with old data.
        // User can click refresh or use TaskCenter.
      }
      
      if (failedCount > 0) {
        message.error(`有 ${failedCount} 个代理商触发同步失败`);
      }
      
    } catch (error) {
      console.error('同步请求失败:', error);
      message.error('同步请求失败');
    } finally {
      setSyncing(false);
    }
  };

  const handleSelectAll = () => {
    let targetAgents = agents;
    if (agentSearchText) {
        const lower = agentSearchText.toLowerCase();
        targetAgents = agents.filter(a => 
            (a.name && a.name.toLowerCase().includes(lower)) || 
            (a.root_advertiser_id && String(a.root_advertiser_id).toLowerCase().includes(lower))
        );
    }
    
    const newCheckedKeys = new Set(checkedKeys);
    targetAgents.forEach(a => newCheckedKeys.add(`agent-${a.id}`));
    
    onCheck(Array.from(newCheckedKeys));
  };

  const handleInverseSelection = () => {
    let targetAgents = agents;
    if (agentSearchText) {
        const lower = agentSearchText.toLowerCase();
        targetAgents = agents.filter(a => 
            (a.name && a.name.toLowerCase().includes(lower)) || 
            (a.root_advertiser_id && String(a.root_advertiser_id).toLowerCase().includes(lower))
        );
    }
    
    const targetIds = targetAgents.map(a => a.id);
    const newIdsSet = new Set(agentIds);
    
    targetIds.forEach(id => {
        if (newIdsSet.has(id)) {
            newIdsSet.delete(id);
        } else {
            newIdsSet.add(id);
        }
    });
    
    const newCheckedKeys: React.Key[] = [];
    newIdsSet.forEach(id => newCheckedKeys.push(`agent-${id}`));
    
    // Reuse onCheck logic to update state and load data
    onCheck(newCheckedKeys);
  };

  const onCheck = (checked: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }) => {
    const keys = Array.isArray(checked) ? checked : checked.checked;
    setCheckedKeys(keys);
    
    const newAgentIds: number[] = [];
    keys.forEach(key => {
        if (typeof key === 'string' && key.startsWith('agent-')) {
            newAgentIds.push(parseInt(key.replace('agent-', '')));
        }
    });
    
    // Only reload if ids changed
    // Simple set compare
    const sortedOld = [...agentIds].sort().join(',');
    const sortedNew = [...newAgentIds].sort().join(',');
    
    if (sortedOld !== sortedNew) {
        loadData(newAgentIds);
    }
  };

  const handleLoginSuccess = (token: string, user: string) => {
    setIsAuthenticated(true);
    setUsername(user);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('username');
    setIsAuthenticated(false);
    setUsername('');
    message.success('已退出登录');
  };

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin size="large" /></div>}>
        <Login onLoginSuccess={handleLoginSuccess} />
      </Suspense>
    );
  }

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  // 客户列表侧栏内容（桌面 Sider 与手机 Drawer 共用）
  const renderSiderContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '20px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#86868b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>客户列表</h2>
            <span style={{ fontSize: '12px', color: '#86868b' }}>{agents.length} 个账户</span>
        </div>

        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            placeholder="搜索客户或二代ID"
            value={agentIds}
            onChange={(newIds) => {
              const newCheckedKeys = newIds.map(id => `agent-${id}`);
              onCheck(newCheckedKeys);
              // Clear search text after selection to show all again or allow new search
              setAgentSearchText('');
            }}
            searchValue={agentSearchText}
            onSearch={setAgentSearchText}
            filterOption={() => true}
            options={agents.map(a => ({
                label: a.name,
                value: a.id,
            })).filter(opt => {
                if (!agentSearchText) return true;
                const lower = agentSearchText.toLowerCase();
                const agent = agents.find(a => a.id === opt.value);
                return (
                    (agent?.name && agent.name.toLowerCase().includes(lower)) ||
                    (agent?.root_advertiser_id && String(agent.root_advertiser_id).toLowerCase().includes(lower))
                );
            })}
            maxTagCount={1}
            maxTagPlaceholder={(omitted) => `+ ${omitted.length} ...`}
            dropdownRender={(menu) => (
                <>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <Space style={{ padding: '0 8px 4px', justifyContent: 'space-between', width: '100%' }}>
                    <Space>
                        <Button type="text" size="small" onClick={handleSelectAll}>
                            全选
                        </Button>
                        <Button type="text" size="small" onClick={handleInverseSelection}>
                            反选
                        </Button>
                    </Space>
                    <Button type="text" size="small" onClick={() => onCheck([])}>
                        清空
                    </Button>
                </Space>
                </>
            )}
          />
          <Button
            type="primary"
            icon={<SafetyCertificateOutlined />}
            onClick={() => { setAgentManagerVisible(true); setSiderVisible(false); }}
            block
            style={{ borderRadius: 8, height: 36 }}
          >
            新增客户
          </Button>
          <Button
            icon={<SyncOutlined spin={syncing} />}
            onClick={handleSyncAdvertisers}
            block
            disabled={agentIds.length === 0 || syncing}
            style={{ borderRadius: 8, height: 36 }}
          >
            {syncing ? '同步中...' : '同步广告主'}
          </Button>
        </Space>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '8px 12px' }}>
        <Spin spinning={loading}>
            <Tree
            checkable
            showLine={{ showLeafIcon: false }}
            defaultExpandAll
            treeData={treeData}
            checkedKeys={checkedKeys}
            onCheck={onCheck}
            style={{ background: 'transparent' }}
            />
        </Spin>
      </div>
    </div>
  );

  return (
    <Layout style={{ height: '100vh', background: '#f5f5f7' }}>
      <Header style={{ 
        padding: '0 24px', 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        width: '100%'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setSiderVisible(true)}
                style={{ marginRight: 8, color: '#1d1d1f' }}
              />
            )}
            <div style={{ 
                width: 32, 
                height: 32, 
                background: '#0066cc', 
                borderRadius: 8, 
                marginRight: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 'bold'
            }}>V</div>
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1d1d1f' }}>vivo 资质平台</h1>
        </div>
        <Space size="large">
          <Button
            type="text"
            icon={<SyncOutlined />}
            onClick={() => setTaskCenterVisible(true)}
            style={{ color: '#1d1d1f' }}
          >
            {!isMobile && '任务中心'}
          </Button>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#e5e5e5', color: '#1d1d1f' }} />
              {!isMobile && <span style={{ color: '#1d1d1f', fontWeight: 500 }}>{username}</span>}
            </Space>
          </Dropdown>
        </Space>
      </Header>
      <Layout style={{ background: 'transparent' }}>
        {!isMobile ? (
          <Sider width={isDesktop ? 280 : 220} theme="light" style={{ borderRight: '1px solid rgba(0,0,0,0.05)' }}>
            {renderSiderContent()}
          </Sider>
        ) : (
          <Drawer
            placement="left"
            open={siderVisible}
            onClose={() => setSiderVisible(false)}
            width={300}
            title="客户列表"
            styles={{ body: { padding: 0, height: '100%' } }}
          >
            {renderSiderContent()}
          </Drawer>
        )}
        <Content style={{ padding: isMobile ? 12 : 24, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {noAgentHint ? (
            <Alert
              type="info"
              showIcon
              message="暂无代理商"
              description="请先在 backend 目录下执行 python init_db.py 初始化数据（会创建测试代理商与 admin 用户），然后刷新本页。"
              style={{ marginBottom: 16, borderRadius: 12 }}
            />
          ) : null}
          <div className="glass-panel" style={{ flex: 1, borderRadius: 16, padding: isMobile ? 12 : 24, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Suspense fallback={<TableSkeleton />}>
              <Spin spinning={tableLoading} tip="加载广告主数据...">
                <AdvertiserTable 
                    agentIds={agentIds}
                    advertisers={selectedAdvertisers}
                    onRefresh={() => loadData()}
                />
              </Spin>
            </Suspense>
          </div>
        </Content>
      </Layout>

      <Suspense fallback={null}>
        <AgentManagerModal
          visible={agentManagerVisible}
          onClose={() => setAgentManagerVisible(false)}
          onRefresh={() => loadData()}
        />
      </Suspense>

      <Suspense fallback={null}>
        <TaskCenter
          visible={taskCenterVisible}
          onClose={() => setTaskCenterVisible(false)}
        />
      </Suspense>
    </Layout>
  );
};

export default App;
